// ABOUTME: Hook previewing a bulk change scope (POST /scope-preview): the count and the first nodes, debounced.
// ABOUTME: Refetches when the scope changes; answers of an older scope are dropped by aborting their request.

import { useEffect, useState } from 'react';

import { previewScope } from '../api/findReplaceApi';
import { BulkChangeScope, ScopePreview } from '../types';

export const SCOPE_PREVIEW_DEBOUNCE_MS = 400;
/** Nodes the recap lists under the count. */
export const SCOPE_PREVIEW_NODES = 5;

export type ScopePreviewState = {
    preview: ScopePreview | null;
    loading: boolean;
    error: Error | null;
};

/**
 * `scope` null (no hierarchy yet) previews nothing. While a changed scope is counted the last
 * answer stays, so the recap shows it greyed out instead of going blank; a failed preview clears it.
 */
export function useScopePreview(scope: BulkChangeScope | null): ScopePreviewState {
    const [preview, setPreview] = useState<ScopePreview | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    // Callers build the scope inline: the serialized form keeps the fetch stable.
    const scopeKey = scope ? JSON.stringify(scope) : null;

    useEffect(() => {
        if (!scopeKey) {
            setPreview(null);
            setLoading(false);
            return undefined;
        }
        const controller = new AbortController();
        setLoading(true);
        const timeout = setTimeout(() => {
            previewScope(
                JSON.parse(scopeKey),
                { skip: 0, limit: SCOPE_PREVIEW_NODES },
                controller.signal,
            )
                .then(data => {
                    setPreview(data);
                    setError(null);
                })
                .catch(e => {
                    if (controller.signal.aborted) return;
                    // The previous scope's count no longer applies: Next stays off until one loads.
                    setPreview(null);
                    setError(e instanceof Error ? e : new Error(String(e)));
                })
                .finally(() => {
                    if (!controller.signal.aborted) setLoading(false);
                });
        }, SCOPE_PREVIEW_DEBOUNCE_MS);
        return () => {
            clearTimeout(timeout);
            controller.abort();
        };
    }, [scopeKey]);

    return { preview, loading, error };
}
